/**
 * Duenne Huelle um die File System Access API (F-46).
 *
 * Eigene, schmale Typen statt `lib.dom`: Dort fehlen `showDirectoryPicker`,
 * `queryPermission` und `requestPermission`, und `values()` gibt es nur mit
 * `DOM.AsyncIterable`, das die tsconfig nicht laedt. Ein echter Handle wird
 * einmal beim Auswaehlen auf diese Typen gecastet; Tests reichen einen
 * kleinen Nachbau herein.
 *
 * Wichtig fuer Chrome: `ordnerAuswaehlen` und `freigabeAnfragen` brauchen
 * eine Nutzeraktion (Klick), sonst wirft Chrome einen SecurityError. Sie
 * duerfen deshalb nur aus Klick-Handlern aufgerufen werden.
 * `freigabePruefen` fragt nie nach und ist ueberall erlaubt.
 */
import { istAutoDatei } from '@/domain/sicherung/autoSicherung';

export type Freigabe = 'granted' | 'prompt' | 'denied';

interface Rechte {
  mode: 'readwrite';
}

export interface DateiInfo {
  readonly size: number;
  readonly lastModified: number;
}

export interface Schreiber {
  write(daten: string): Promise<void>;
  close(): Promise<void>;
  abort(): Promise<void>;
}

export interface DateiHandle {
  readonly kind: 'file';
  readonly name: string;
  createWritable(): Promise<Schreiber>;
  getFile(): Promise<DateiInfo>;
}

export interface OrdnerHandle {
  readonly kind: 'directory';
  readonly name: string;
  queryPermission?(d: Rechte): Promise<Freigabe>;
  requestPermission?(d: Rechte): Promise<Freigabe>;
  getFileHandle(name: string, o?: { create?: boolean }): Promise<DateiHandle>;
  removeEntry(name: string): Promise<void>;
  values(): AsyncIterable<DateiHandle | { readonly kind: 'directory'; readonly name: string }>;
}

export interface OrdnerDatei {
  name: string;
  bytes: number;
  geaendert: number;
}

export type FehlerArt = 'freigabe' | 'nicht_gefunden' | 'voll' | 'schreiben' | 'unbekannt';

/** Die geschriebene Datei ist nicht so gross wie erwartet. */
export class SchreibFehler extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SchreibFehler';
  }
}

type Picker = (o: { id: string; mode: 'readwrite'; startIn: OrdnerHandle | 'documents' }) => Promise<unknown>;

function picker(): Picker | undefined {
  if (typeof window === 'undefined') return undefined;
  const p = (window as unknown as { showDirectoryPicker?: unknown }).showDirectoryPicker;
  return typeof p === 'function' ? (p as Picker) : undefined;
}

/**
 * Gibt es die Ordnerauswahl? Geprueft wird der Picker selbst, NICHT die
 * Klasse `FileSystemDirectoryHandle`: Chrome auf Android kennt die Klasse
 * (fuer den privaten Ursprungsspeicher), aber keinen Picker. Ausserhalb
 * sicherer Kontexte (https, localhost) fehlt der Picker ohnehin.
 */
export function ordnerUnterstuetzt(): boolean {
  return picker() !== undefined;
}

/**
 * Ordner waehlen lassen - NUR aus einem Klick-Handler. Fragt gleich nach
 * Schreibrecht, sonst kaeme beim ersten Schreiben eine zweite Abfrage.
 * Abbrechen ergibt `null`, alles andere wird weitergeworfen.
 */
export async function ordnerAuswaehlen(startIn?: OrdnerHandle): Promise<OrdnerHandle | null> {
  if (!picker()) return null;
  const w = window as unknown as { showDirectoryPicker: Picker };
  try {
    const h = await w.showDirectoryPicker({ id: 'ole-privat-sicherung', mode: 'readwrite', startIn: startIn ?? 'documents' });
    return h as OrdnerHandle;
  } catch (err) {
    if (fehlerName(err) === 'AbortError') return null;
    throw err;
  }
}

/** Stand der Freigabe, ohne nachzufragen - ueberall erlaubt. */
export async function freigabePruefen(o: OrdnerHandle): Promise<Freigabe> {
  return (await o.queryPermission?.({ mode: 'readwrite' })) ?? 'granted';
}

/**
 * Freigabe erbitten - NUR aus einem Klick-Handler und dort als ERSTES
 * `await`. Ist sie schon erteilt, fragt Chrome nicht nochmal.
 */
export async function freigabeAnfragen(o: OrdnerHandle): Promise<Freigabe> {
  return (await o.requestPermission?.({ mode: 'readwrite' })) ?? 'granted';
}

/**
 * Schreibt `text` als Datei und prueft danach die Groesse in BYTES (Umlaute
 * sind in UTF-8 mehrere Bytes). Chrome schreibt zuerst in eine `.crswap`-Datei
 * und tauscht sie erst bei `close()` aus - eine halbe Datei entsteht so nicht.
 * Gibt die Groesse zurueck.
 */
export async function dateiSchreiben(o: OrdnerHandle, name: string, text: string): Promise<number> {
  const erwartet = new Blob([text]).size;
  // `create: true` legt sofort eine leere Datei an - scheitert das Schreiben,
  // wird sie unten wieder entfernt, damit keine leere "Sicherung" liegen bleibt.
  const datei = await o.getFileHandle(name, { create: true });
  try {
    const schreiber = await datei.createWritable();
    try {
      await schreiber.write(text);
      await schreiber.close();
    } catch (err) {
      await schreiber.abort().catch(() => undefined);
      throw err;
    }
  } catch (err) {
    await leiseEntfernen(o, name, async () => (await datei.getFile()).size === 0);
    throw err;
  }
  const geschrieben = (await datei.getFile()).size;
  if (geschrieben !== erwartet) {
    // Unvollstaendig: Diese Datei ist keine brauchbare Sicherung.
    await leiseEntfernen(o, name, async () => true);
    throw new SchreibFehler(`${name}: ${geschrieben} statt ${erwartet} Bytes geschrieben.`);
  }
  return geschrieben;
}

/** Eine eben selbst angelegte, kaputte Datei entfernen - Fehler dabei schlucken. */
async function leiseEntfernen(o: OrdnerHandle, name: string, wenn: () => Promise<boolean>): Promise<void> {
  try {
    if (istAutoDatei(name) && (await wenn())) await o.removeEntry(name);
  } catch {
    // Bleibt sie liegen, raeumt `aufraeumListe` sie beim naechsten Mal mit weg.
  }
}

/** Nur eigene automatische Sicherungen, nur Dateien. */
export async function dateienAuflisten(o: OrdnerHandle): Promise<OrdnerDatei[]> {
  const dateien: OrdnerDatei[] = [];
  for await (const e of o.values()) {
    if (e.kind !== 'file' || !istAutoDatei(e.name)) continue;
    const info = await e.getFile();
    dateien.push({ name: e.name, bytes: info.size, geaendert: info.lastModified });
  }
  return dateien;
}

/**
 * Loescht die genannten Dateien nacheinander. Die Namen kommen fertig
 * herein: waehrend `values()` laeuft wird nie geloescht.
 */
export async function dateienLoeschen(o: OrdnerHandle, namen: readonly string[]): Promise<void> {
  for (const name of namen) {
    if (istAutoDatei(name)) await o.removeEntry(name);
  }
}

function fehlerName(err: unknown): string | undefined {
  if (typeof err !== 'object' || err === null) return undefined;
  const n = (err as { name?: unknown }).name;
  return typeof n === 'string' ? n : undefined;
}

/** Fehler grob einordnen, damit die Oberflaeche das Richtige anbietet. */
export function fehlerArt(err: unknown): FehlerArt {
  if (err instanceof SchreibFehler) return 'schreiben';
  switch (fehlerName(err)) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'freigabe';
    case 'NotFoundError':
      return 'nicht_gefunden';
    case 'QuotaExceededError':
      return 'voll';
    case 'NoModificationAllowedError':
    case 'InvalidModificationError':
    case 'InvalidStateError':
      return 'schreiben';
    default:
      return 'unbekannt';
  }
}
